import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { runBigQueryQuery } from "../bigquery/BigQueryClient";
import ExperimentReport from "../components/ExperimentReport";
import ExperimentCustomPlots from "../components/ExperimentCustomPlots";

interface ExperimentSummary {
  id: string;
  name: string;
  status: number;
  owner: string | null;
  description: string | null;
  outcome: string | null;
  traffic_per_flight: number | null;
  start_time: string | null;
  end_time: string | null;
  set_id: string | null;
}

interface ResultRun {
  request_id: string;
  params: string | null;
  analysis: string | null;
  received_at: string | null;
}

const STATUS_LABELS: Record<number, string> = { 0: "Paused", 1: "Active", 2: "Archived", 3: "Concluded" };
const STATUS_MODIFIER: Record<number, string> = { 0: "paused", 1: "active", 2: "archived", 3: "concluded" };

const dateFmt = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function formatDateTime(value: string | null): string {
  if (!value) return "–";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "–";
  return dateFmt.format(d);
}

const dateShortFmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });

function formatDateShort(value: string | null): string {
  if (!value) return "–";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "–";
  return dateShortFmt.format(d);
}

function formatTraffic(value: number | null): string {
  if (value == null) return "–";
  return `${Math.round(value * 100)}%`;
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function buildSummarySQL(id: string): string {
  const safeId = Number(id);
  if (!Number.isInteger(safeId)) throw new Error(`Invalid experiment id: ${id}`);
  return `
    SELECT
      CAST(e.id AS STRING) AS id,
      e.name,
      e.status,
      COALESCE(NULLIF(e.owner_team, ''), u.name) AS owner,
      e.description,
      e.outcome,
      e.traffic_per_flight,
      FORMAT_TIMESTAMP('%Y-%m-%dT%H:%M:%SZ', e.start_time) AS start_time,
      FORMAT_TIMESTAMP('%Y-%m-%dT%H:%M:%SZ', e.end_time) AS end_time,
      CAST(e.global_experiment_set_id AS STRING) AS set_id
    FROM \`martin-test-datalab.postgres.global_experiments\` e
    LEFT JOIN \`martin-test-datalab.postgres.users\` u ON e.owner_id = u.id
    WHERE e.id = ${safeId}
  `;
}

function buildResultsSQL(id: string): string {
  const safeId = Number(id);
  if (!Number.isInteger(safeId)) throw new Error(`Invalid experiment id: ${id}`);
  return `
    SELECT
      request_id,
      params,
      analysis,
      FORMAT_TIMESTAMP('%Y-%m-%dT%H:%M:%SZ', received_at) AS received_at
    FROM \`martin-test-datalab.postgres.global_experiment_results\`
    WHERE global_experiment_id = ${safeId}
    ORDER BY received_at DESC
  `;
}

/** Pull a one-line status out of an analysis JSON blob without rendering the full payload. */
function summarizeAnalysis(analysis: string | null): { ok: boolean; label: string } {
  if (!analysis) return { ok: false, label: "No analysis" };
  try {
    const parsed = JSON.parse(analysis);
    if (parsed.error) return { ok: false, label: `Error: ${String(parsed.error).slice(0, 80)}` };
    if (parsed.kpis) {
      const variants = Object.keys(parsed.kpis).length;
      return { ok: true, label: `KPIs for ${variants} variant${variants === 1 ? "" : "s"}` };
    }
    return { ok: true, label: "Analysis available" };
  } catch {
    return { ok: false, label: "Unparseable analysis" };
  }
}

function summarizeParams(params: string | null): string {
  if (!params) return "";
  try {
    const parsed = JSON.parse(params);
    const variants = Array.isArray(parsed.flight_variants) ? parsed.flight_variants.join(" vs ") : "";
    return variants ? `Flights ${variants}` : "";
  } catch {
    return "";
  }
}

export default function ExperimentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, requiresAuth } = useAuth();
  const [summary, setSummary] = useState<ExperimentSummary | null>(null);
  const [results, setResults] = useState<ResultRun[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    if (requiresAuth && !isAuthenticated) return;
    setIsLoading(true);
    setError(null);

    let summarySQL: string;
    let resultsSQL: string;
    try {
      summarySQL = buildSummarySQL(id);
      resultsSQL = buildResultsSQL(id);
    } catch (e) {
      setError(String(e));
      setIsLoading(false);
      return;
    }

    Promise.all([runBigQueryQuery(summarySQL), runBigQueryQuery(resultsSQL)])
      .then(([summaryResults, resultRows]) => {
        setSummary((summaryResults[0] as unknown as ExperimentSummary) ?? null);
        setResults(resultRows as unknown as ResultRun[]);
      })
      .catch(err => setError(String(err)))
      .finally(() => setIsLoading(false));
  }, [id, isAuthenticated, requiresAuth]);

  if (isLoading) {
    return <div className="entity-list__loading">Loading…</div>;
  }

  if (error) {
    return (
      <div className="entity-list__error">
        <p>Something went wrong loading this experiment. Please try refreshing the page.</p>
        <p className="entity-list__error-detail">{error}</p>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="entity-detail">
        <Link to="/experiments" className="entity-detail__back">
          ← Experiments
        </Link>
        <p className="entity-list__empty">Experiment {id} not found.</p>
      </div>
    );
  }

  return (
    <div className="entity-detail">
      <div className="audience-report__topbar">
        <Link to="/experiments" className="entity-detail__back">
          ← Experiments
        </Link>
      </div>

      {/* Page title sits above the cards, on the page background. */}
      <h1 className="experiment-detail__title">
        {summary.name?.trim() || `Experiment ${summary.id}`}
        <span className={`experiment-status experiment-status--${STATUS_MODIFIER[summary.status] ?? "unknown"}`}>
          {STATUS_LABELS[summary.status] ?? `Status ${summary.status}`}
        </span>
        <span className="experiment-beta" title="Exploratory draft — work in progress, not final">
          Beta
        </span>
      </h1>
      <p className="experiment-detail__subtitle">
        Experiment reporting is an exploratory draft — per-flight KPIs come from the WPR metrics (BigQuery) source over
        the experiment window; metrics and layout are work in progress, not final.
      </p>

      {/* ── Report (KPIs from Pivot). Meta renders in the overview card; description
             renders in its own box to the right of it. ── */}
      <ExperimentReport
        experimentId={summary.id}
        startTime={summary.start_time}
        endTime={summary.end_time}
        header={
          <div className="experiment-detail__meta">
            {summary.owner && (
              <span>
                <b>Owner</b>
                {titleCase(summary.owner)}
              </span>
            )}
            <span>
              <b>Traffic / flight</b>
              {formatTraffic(summary.traffic_per_flight)}
            </span>
            <span>
              <b>Window</b>
              {formatDateShort(summary.start_time)} → {formatDateShort(summary.end_time)}
            </span>
            {summary.set_id && (
              <span>
                <b>Set</b>#{summary.set_id}
              </span>
            )}
          </div>
        }
        description={
          summary.description || summary.outcome ? (
            <>
              {summary.description && <p className="experiment-detail__lead">{summary.description}</p>}
              {summary.outcome && (
                <p className="experiment-detail__lead">
                  <span className="experiment-detail__lead-tag">Outcome</span>
                  {summary.outcome}
                </p>
              )}
            </>
          ) : null
        }
      />

      {/* ── Analysis runs ──
          The live source of truth is the ford-backed evaluation API, surfaced in Admin.
          BigQuery only has a stale mirror of `global_experiment_results` (synced up to
          2025-12-08), so we link out to Admin and only show mirror rows as historical. */}
      <div className="entity-detail__card">
        <p className="entity-detail__card-label">Analysis runs</p>
        <p className="experiment-detail__prose">
          Evaluation runs are managed by the ford pipeline and shown in{" "}
          <a
            href={`https://admin.remerge.io/admin/global-experiments/${summary.id}/analysis`}
            target="_blank"
            rel="noopener noreferrer"
            className="entity-list__link"
          >
            Admin → Analysis ↗
          </a>
          .
        </p>
        {results.length > 0 && (
          <>
            <p className="experiment-report__hint">
              Historical runs from the BigQuery mirror (synced up to 8 Dec 2025 — may be incomplete):
            </p>
            <table className="entity-list__table">
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Flights</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {results.map(r => {
                  const a = summarizeAnalysis(r.analysis);
                  return (
                    <tr key={r.request_id} className="entity-list__row">
                      <td className="entity-list__col--audiences">{formatDateTime(r.received_at)}</td>
                      <td className="entity-list__col--audiences">{summarizeParams(r.params) || "–"}</td>
                      <td className="entity-list__col--audiences">
                        <span className={`experiment-status experiment-status--${a.ok ? "active" : "paused"}`}>
                          {a.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* ── Custom charts ──
          Per-flight charts built from the WPR-metrics (BigQuery) source, sliced on
          primary_prediction_flight_id and scoped to the experiment window — one chart
          per prediction flight, side by side. */}
      <div className="entity-detail__card">
        <p className="entity-detail__card-label">Custom charts</p>
        <p className="experiment-detail__prose">
          Build a chart to compare a flight-supported metric across this experiment&rsquo;s prediction flights, side by
          side. Data comes from the WPR metrics source and is scoped to the experiment window.
        </p>
        <ExperimentCustomPlots experimentId={summary.id} startTime={summary.start_time} endTime={summary.end_time} />
      </div>
    </div>
  );
}
